/*
 * <scentt-hero> \u2013 hero slideshow: autoplay (only while visible), dots, arrows, swipe,
 * pause on hover / focus / pause button, theme-editor block selection.
 * Without JavaScript the first slide simply stays visible.
 */
(() => {
  if (customElements.get('scentt-hero')) return;
  const S = window.Scentt && Object.isFrozen(window.Scentt) && typeof window.Scentt.html === 'function' ? window.Scentt : null;
  const reduced = () => (S ? S.reducedMotion : false);
  const int = (v, d, min, max) => (S ? S.int(v, d, min, max) : d);

  class ScenttHero extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('.scentt-hero__slide'));
      if (this.slides.length < 2) return;
      this.dots = Array.from(this.querySelectorAll('[data-hero-dot]'));
      this.copies = Array.from(this.querySelectorAll('.scentt-hero__copy'));
      this.staticCopy = this.querySelector('.scentt-hero__static');
      this.pauseBtn = this.querySelector('[data-hero-pause]');
      this.interval = int(this.dataset.interval, 6, 2, 15) * 1000;
      this.auto = this.dataset.autoplay === 'true' && !reduced();
      this.index = Math.max(0, this.slides.findIndex((s) => s.classList.contains('is-active')));
      this.holds = new Set();
      this.visible = false;
      if (!this.auto && this.pauseBtn) this.pauseBtn.hidden = true;

      this.onClick = (e) => {
        const t = e.target;
        if (t.closest('[data-hero-next]')) this.go(this.index + 1, true);
        else if (t.closest('[data-hero-prev]')) this.go(this.index - 1, true);
        else if (t.closest('[data-hero-pause]')) this.toggleUserPause();
        else {
          const dot = t.closest('[data-hero-dot]');
          if (dot) this.go(int(dot.getAttribute('data-hero-dot'), 0, 0, this.slides.length - 1), true);
        }
      };
      this.addEventListener('click', this.onClick);

      if (this.dataset.pause === 'true') {
        this.addEventListener('mouseenter', () => this.hold('hover'));
        this.addEventListener('mouseleave', () => this.release('hover'));
      }
      this.addEventListener('focusin', () => this.hold('focus'));
      this.addEventListener('focusout', (e) => { if (!this.contains(e.relatedTarget)) this.release('focus'); });

      let startX = null;
      this.addEventListener('touchstart', (e) => { startX = e.touches[0].clientX; }, { passive: true });
      this.addEventListener('touchend', (e) => {
        if (startX === null) return;
        const dx = e.changedTouches[0].clientX - startX;
        startX = null;
        if (Math.abs(dx) > 40) this.go(this.index + (dx < 0 ? 1 : -1), true);
      }, { passive: true });

      this.onVisibility = () => this.restart();
      document.addEventListener('visibilitychange', this.onVisibility);

      this.onBlockSelect = (e) => {
        const slide = this.slides.find((s) => s.dataset.blockId === (e.detail && e.detail.blockId));
        if (!slide) return;
        this.hold('editor');
        this.go(this.slides.indexOf(slide), true);
      };
      this.onBlockDeselect = () => this.release('editor');
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      document.addEventListener('shopify:block:deselect', this.onBlockDeselect);

      /* Only run the timer while the hero is on screen. */
      if ('IntersectionObserver' in window) {
        this.io = new IntersectionObserver((entries) => {
          this.visible = entries.some((en) => en.isIntersecting);
          this.restart();
        }, { threshold: 0.2 });
        this.io.observe(this);
      } else {
        this.visible = true;
        this.restart();
      }
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      clearTimeout(this.leaveTimer);
      if (this.io) this.io.disconnect();
      document.removeEventListener('visibilitychange', this.onVisibility);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
    }

    get paused() { return this.holds.size > 0; }

    hold(reason) { this.holds.add(reason); this.restart(); }

    release(reason) { this.holds.delete(reason); this.restart(); }

    toggleUserPause() {
      const on = !this.holds.has('user');
      if (on) this.hold('user'); else this.release('user');
      if (this.pauseBtn) {
        this.pauseBtn.setAttribute('aria-pressed', String(on));
        const label = on ? this.dataset.labelPlay : this.dataset.labelPause;
        if (label) this.pauseBtn.setAttribute('aria-label', label);
      }
    }

    go(target, manual = false) {
      const n = this.slides.length;
      const i = ((target % n) + n) % n;
      if (i === this.index && !manual) return;
      const prev = this.index;
      this.slides.forEach((s, k) => {
        s.classList.toggle('is-leaving', k === prev && k !== i);
        s.classList.toggle('is-active', k === i);
        if (k === i) s.removeAttribute('aria-hidden'); else s.setAttribute('aria-hidden', 'true');
      });
      clearTimeout(this.leaveTimer);
      this.leaveTimer = setTimeout(() => this.slides.forEach((s) => s.classList.remove('is-leaving')), 2100);
      this.index = i;
      const hasCopy = this.slides[i].hasAttribute('data-has-copy');
      this.copies.forEach((c) => { c.hidden = c.getAttribute('data-copy') !== String(i); });
      if (this.staticCopy) this.staticCopy.hidden = hasCopy;
      this.dots.forEach((d, k) => {
        d.classList.remove('is-active');
        if (k === i) {
          void d.offsetWidth; /* restart the progress line animation */
          d.classList.add('is-active');
          d.setAttribute('aria-current', 'true');
        } else {
          d.removeAttribute('aria-current');
        }
      });
      this.dispatchEvent(new CustomEvent('scentt:hero-change', { detail: { index: i }, bubbles: true }));
      this.restart();
    }

    restart() {
      clearTimeout(this.timer);
      const running = this.auto && !this.paused && this.visible && !document.hidden;
      this.classList.toggle('is-paused', !running);
      this.dots.forEach((d) => d.classList.toggle('is-static', !this.auto));
      if (running) this.timer = setTimeout(() => this.go(this.index + 1), this.interval);
    }
  }

  customElements.define('scentt-hero', ScenttHero);
})();
